import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle, AlertCircle, X } from "lucide-react";

/**
 * Reusable Toast Notification
 *
 * Usage:
 *   const [toast, setToast] = useState(null);
 *   const showToast = (type, message) => {
 *     setToast({ type, message });
 *     setTimeout(() => setToast(null), 4500);
 *   };
 *   <Toast toast={toast} onClose={() => setToast(null)} />
 *
 * @param {{ type: "success" | "error", message: string } | null} toast
 * @param {() => void} onClose
 */
export default function Toast({ toast, onClose }) {
  return (
    <AnimatePresence>
      {toast && (
        <div className="fixed inset-0 z-50 flex items-end justify-center pb-6 px-4 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className={`w-full max-w-md pointer-events-auto rounded-2xl shadow-2xl overflow-hidden border ${
              toast.type === "success"
                ? "bg-green-600 border-green-500"
                : "bg-red-600 border-red-500"
            }`}
          >
            <div className="flex items-center gap-4 p-5 text-white">
              <div className="flex-shrink-0">
                {toast.type === "success" ? (
                  <CheckCircle size={28} />
                ) : (
                  <AlertCircle size={28} />
                )}
              </div>
              <div className="flex-1 text-[15px] font-medium leading-tight">
                {toast.message}
              </div>
              <button
                onClick={onClose}
                className="flex-shrink-0 opacity-80 hover:opacity-100 transition-opacity"
              >
                <X size={22} />
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
